import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { fetchSynonymsByChapter } from '../api/client';
import { DEFAULT_LANGUAGE } from '../utils/defaultLanguage';

// Chapter-level cache: "language:chapter" → slokaNumber → sorted word array
const synonymCache = new Map();
const synonymInFlight = new Map();

async function loadSynonymsForChapter(language, chapterNumber) {
  const key = `${language}:${chapterNumber}`;
  if (synonymCache.has(key)) return synonymCache.get(key);
  if (synonymInFlight.has(key)) return synonymInFlight.get(key);

  const promise = (async () => {
    const records = await fetchSynonymsByChapter(language, chapterNumber);
    const bySloka = new Map();
    for (const r of records) {
      if (!bySloka.has(r.slokaNumber)) bySloka.set(r.slokaNumber, []);
      bySloka.get(r.slokaNumber).push(r);
    }
    synonymCache.set(key, bySloka);
    return bySloka;
  })();

  synonymInFlight.set(key, promise);
  try {
    return await promise;
  } finally {
    synonymInFlight.delete(key);
  }
}

/**
 * Renders a word-by-word Sanskrit breakdown for a single verse.
 *
 * Props:
 *   chapterNumber  number
 *   slokaNumber    number
 *   language       string (defaults to DEFAULT_LANGUAGE)
 *   defaultOpen    bool   (default true)
 *   noToggle       bool   — hide toggle button, always show chips (default false)
 *   className      string
 */
const WordBreakdown = ({
  chapterNumber,
  slokaNumber,
  language = DEFAULT_LANGUAGE,
  defaultOpen = true,
  noToggle = false,
  className = ''
}) => {
  const [open, setOpen] = useState(defaultOpen || noToggle);
  const [words, setWords] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const chapter = Number(chapterNumber);
  const sloka = Number(slokaNumber);
  const canLoad = open && Number.isFinite(chapter) && Number.isFinite(sloka);

  useEffect(() => {
    if (!canLoad) {
      setWords(null);
      setError(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setWords(null);
    setError(null);
    setLoading(true);

    loadSynonymsForChapter(language, chapter)
      .then((bySloka) => {
        if (!cancelled) setWords(bySloka.get(sloka) || []);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load word breakdown.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [canLoad, language, chapter, sloka]);

  useEffect(() => {
    setOpen(defaultOpen || noToggle);
  }, [defaultOpen, noToggle]);

  const chips = (
    <div className="word-chips-container">
      {loading && <p className="word-breakdown-loading">Loading…</p>}
      {error && <p className="word-breakdown-error">{error}</p>}
      {!loading && !error && words !== null && words.length === 0 && (
        <p className="word-breakdown-empty">No word breakdown available for this verse.</p>
      )}
      {!loading && !error && words && words.length > 0 && (
        <div className="word-chips">
          {words.map((w, i) => (
            <div key={i} className="word-chip">
              <span className="word-chip-sanskrit">{w.word}</span>
              <span className="word-chip-meaning">{w.meaning}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (noToggle) {
    return (
      <div className={`word-breakdown ${className}`}>
        {chips}
      </div>
    );
  }

  return (
    <div className={`word-breakdown ${className}`}>
      <button
        type="button"
        className="word-breakdown-toggle"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span>Word-by-word</span>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {open && chips}
    </div>
  );
};

export default WordBreakdown;
